import { FC } from 'react';
import { useFragment } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import Link from '@mui/material/Link';
import EntityTableRow, { EntityTableRowProps } from 'components/EntityTableRow';
import NonBreakingDate from 'components/NonBreakingDate';
import TableCell from 'components/TableCell';
import PackageDateStatus from 'components/PackageDateStatus';
import PackageDateRowFlight from 'components/PackageDateRowFlight';

import { PackageDateRow_packageDate$key } from '__generated__/PackageDateRow_packageDate.graphql';
import { PackageDateRow_data$key } from '__generated__/PackageDateRow_data.graphql';
import { PackageDateRow_package$key } from '__generated__/PackageDateRow_package.graphql';

type Props = EntityTableRowProps & {
  packageDate: PackageDateRow_packageDate$key;
  data: PackageDateRow_data$key;
  package: PackageDateRow_package$key;
};

const PackageDateRow: FC<Props> = ({ packageDate, ...props }) => {
  const packageDateData = useFragment(
    graphql`
      fragment PackageDateRow_packageDate on PackageDate {
        id
        start
        end
        validTo
        duration
        accommodationPrice

        ...EntityTableRow_entity
        ...PackageDateRowFlight_packageDate
        ...PackageDateStatus_packageDate
      }
    `,
    packageDate
  );

  const data = useFragment(
    graphql`
      fragment PackageDateRow_data on Query {
        ...PackageDateRowFlight_data
      }
    `,
    props.data
  );

  const pack = useFragment(
    graphql`
      fragment PackageDateRow_package on Package {
        status

        ...PackageDateRowFlight_package
      }
    `,
    props.package
  );

  const { id, start, end, validTo, duration, accommodationPrice } =
    packageDateData;

  return (
    <EntityTableRow entity={packageDateData} {...props}>
      <TableCell>
        <PackageDateStatus packageDate={packageDateData} />
      </TableCell>

      <TableCell center>
        <Link href={`/package-dates/${id}`}>
          <NonBreakingDate>{start}</NonBreakingDate> -{' '}
          <NonBreakingDate>{end}</NonBreakingDate>
        </Link>
      </TableCell>

      <TableCell center>
        <NonBreakingDate>{validTo}</NonBreakingDate>
      </TableCell>

      <TableCell center>{duration}</TableCell>

      <PackageDateRowFlight
        packageDate={packageDateData}
        package={pack}
        data={data}
      />

      <TableCell center>
        {accommodationPrice && `€${accommodationPrice}`}
      </TableCell>
    </EntityTableRow>
  );
};

export default PackageDateRow;
